import { Injectable, OnDestroy } from '@angular/core';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { filter, map, tap } from 'rxjs/operators';

import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { useDispatcher, useStore } from '../../../store.decorator';
import {
    CallHandle,
    callHandleActive,
    CallState,
    CallStatus,
    CallStatusNotification,
    CallStatusService,
    HubMethods as TcHubMethods,
} from '../../../teams-call';
import { CaResultNotification, HubMethods, SocketService } from '../../core';
import { CaMeetingRequestContext } from '../core';

import { CaAnalysisDialogComponent } from './ca-analysis-dialog.component';
import { caAnalysisDialogReducer } from './ca-analysis-dialog.reducer';
import { CaAnalysisDialogState } from './ca-analysis-dialog.state';
import { changeTranscriptStatus } from './state/change-transcript-status.action';
import { receiveMeetingRequestContext } from './state/receive-meeting-request-context.action';
import { receiveResultData } from './state/receive-result-data.action';
import { rememberStatusChange } from './state/remember-status-change.action';
import { reset } from './state/reset.action';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class CaAnalysisDialogService implements OnDestroy {
    state: CaAnalysisDialogState;

    private _ref: CmpsDialogRef<CaAnalysisDialogComponent, any>;

    get isOpen(): boolean {
        return this._ref != null && !this._ref.closed;
    }

    get meetingRequestContext(): CaMeetingRequestContext {
        return this.state?.meetingRequestContext;
    }

    constructor(
        private readonly _dialog: CmpsDialog,
        private readonly _socketService: SocketService,
        readonly callStatusService: CallStatusService,
    ) {
        this._setupBindings();

        useStore(this, 'coaching.caAnalysis', caAnalysisDialogReducer);
    }

    ngOnDestroy(): void {
        this.close();
    }

    open(): void {
        if (this.isOpen) {
            return;
        }
        this._ref = this._dialog.open(CaAnalysisDialogComponent, {
            disableOverlayOverflowHidden: true,
            overlayClass: 'cmps-ca-analysis-dialog--overlay-class',
            portalWrapperClass: 'cmps-ca-analysis-dialog--portal-wrapper-class',
            disableBackdrop: true,
            disableClose: true,
            placement: 'top-left',
        });
    }

    close(): void {
        this._ref?.close();
    }

    private _setupBindings(): void {
        this._socketService.didCreate
            .pipe(
                tap(() => {
                    this._bind();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._socketService.didConnect
            .pipe(
                tap(() => {
                    void this._socketService.retrieveCaMeetingRequestContext();
                    void this._socketService.retrieveCurrentCaResult();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _bind(): void {
        this.callStatusService.message
            .pipe(
                filter(
                    (callHdl) =>
                        callHdl.hubMethod === TcHubMethods.OnCurrentCall,
                ),
                map((callHdl) => callHdl.payload as CallHandle | null),
                tap((callHdl) => {
                    if (callHandleActive(callHdl)) {
                        this._meetingStarted();
                        const initialTranscriptState =
                            callHdl?.stateHistory?.find(
                                (e) =>
                                    e.state ===
                                    CallState.InitialTranscriptStreamed,
                            );
                        if (initialTranscriptState != null) {
                            this._initialTranscriptStreamed();
                        }
                    } else {
                        this._meetingEnded();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this.callStatusService.message
            .pipe(
                filter(
                    (noti) =>
                        noti.hubMethod === TcHubMethods.OnCallStatus &&
                        this.callStatusService.callHandle?.callHandleId !=
                            null &&
                        (noti.payload as CallStatusNotification)
                            ?.callHandleId ===
                            this.callStatusService.callHandle?.callHandleId,
                ),
                map((noti) => noti.payload as CallStatusNotification),
                tap((noti) => {
                    useDispatcher(
                        this,
                        rememberStatusChange({ status: noti.status }),
                    );
                    switch (noti.status) {
                        case CallStatus.CREATED:
                            useDispatcher(this, reset({}));
                            break;
                        case CallStatus.MEETING_STARTED:
                            this._meetingStarted();
                            break;
                        case CallStatus.INITIAL_TRANSCRIPT_STREAMED:
                            this._initialTranscriptStreamed();
                            break;
                        case CallStatus.MEETING_ENDED:
                        case CallStatus.ENDED:
                            this._meetingEnded();
                            break;
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._socketService.on(
            HubMethods.OnCaMeetingRequestContext,
            (payload: CaMeetingRequestContext) => {
                console.log(payload);

                useDispatcher(
                    this,
                    receiveMeetingRequestContext({
                        caMeetingRequestContext: payload,
                    }),
                );
            },
        );

        this._socketService.on(
            HubMethods.OnCaResult,
            (payload: CaResultNotification) => {
                console.log(payload);

                if (
                    this.callStatusService.callHandle?.callHandleId == null ||
                    payload?.callHandleId !==
                        this.callStatusService.callHandle?.callHandleId ||
                    this.state.statuses.includes(CallStatus.MEETING_ENDED) ||
                    this.state.statuses.includes(CallStatus.ENDED) ||
                    payload?.caResult == null
                ) {
                    return;
                }

                this.open();

                useDispatcher(
                    this,
                    receiveResultData({ caResult: payload.caResult }),
                );
            },
        );
    }

    private _meetingStarted(): void {
        // see meetingRequestContext?.evaluationEnabled
        if (this.callStatusService.callHandle?.streamTranscript === true) {
            void this._socketService.retrieveCurrentCaResult();
        }
    }

    private _initialTranscriptStreamed(): void {
        useDispatcher(this, changeTranscriptStatus({ status: 'green' }));
    }

    private _meetingEnded(): void {
        this.close();
    }
}
